// ui/DiscardPrompt.jsx: M2.c-11 dealer discard prompt.
//
// Renders when current_state === 'dealer_discard' AND dealer_seat ===
// human_seat. Plain DOM overlay (not a <dialog>, we don't want a
// modal scrim; the player needs the canvas/hand visible to choose a
// card by clicking on the rendered hand from canvas/hand.js).
//
// The Discard button is disabled until a card is selected. Clicking
// it dispatches dealer_discard(cardId) via the reducer.
//
// Accessibility per plan AC: an aria-live region announces the
// discard phase, and the Discard button has explicit text so screen
// readers do not lean on the surrounding context.

import { useEffect, useLayoutEffect, useRef } from 'preact/hooks';
import { _internals as cardInternals } from '../canvas/cards.js';
import { computeDiscardPromptRect, DISCARD_PROMPT_H, DISCARD_PROMPT_SHORT_LABEL_W } from './discard-prompt-position.js';

const { SUIT_NAME } = cardInternals;

function describeCard(cardId) {
  if (!cardId) return '';
  const rank = cardId[0] === 'T' ? 'Ten' : cardId[0];
  const suit = SUIT_NAME[cardId[1]];
  return `${rank} of ${suit}`;
}

/**
 * @param {{
 *   selectedCardId: string|null,
 *   onConfirm: () => void,
 *   layout?: import('../canvas/layout.js').TableLayout|null,
 *   rect?: {x:number, y:number, w:number, h:number}|null,
 *   disabledReason?: string|null,
 *   onMeasure?: ((height:number) => void)|null,
 * }} props
 */
export function DiscardPrompt({ selectedCardId, onConfirm, layout = null, rect = undefined, disabledReason = null, onMeasure = null }) {
  /** @type {{ current: HTMLButtonElement|null }} */
  const buttonRef = useRef(null);
  /** @type {{ current: HTMLDivElement|null }} */
  const boxRef = useRef(null);

  // B3c r3: report the rendered height, so the view places the prompt
  // from its real box. At 320x568 a picked card wraps the label to three
  // lines and the box is 81px, past the 68px the portrait rect reserved
  // (it ran 11px onto the timer ring). A layout effect measures before
  // the first paint; the observer catches every later change (a card
  // picked, a new width).
  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el || !onMeasure || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => onMeasure(Math.ceil(el.getBoundingClientRect().height));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [onMeasure]);

  // Focus the Discard button when a selection is first made so a
  // keyboard user can immediately confirm.
  useEffect(() => {
    if (selectedCardId && buttonRef.current) {
      buttonRef.current.focus();
    }
  }, [selectedCardId]);

  const enabled = !!selectedCardId && !disabledReason;
  const cardDesc = describeCard(selectedCardId);
  // V2 + W1: in the short-landscape composition the in-flow prompt
  // lands below the canvas, outside the viewport. On desktop the same
  // happens on short windows (800x600, 1024x600). Overlay the table
  // instead, like the portrait prompt: absolute inside the stage, above
  // the hand fan, clear of the hand cards, the You plate, the timer
  // ring and the up-card. Otherwise the prompt stays in flow below the
  // canvas.
  // fix6 Q2b: one helper owns every layout, portrait included; the view
  // passes the rect it also gives the trump splash.
  const viewportH = typeof window !== 'undefined' ? window.innerHeight : null;
  const overlayRect = rect !== undefined
    ? rect
    : layout ? computeDiscardPromptRect(layout, DISCARD_PROMPT_H, viewportH) : null;
  // fix6: the landscape and desktop overlays are capped at 300px, where
  // the picked-card label wraps to three lines; at 14px/19px lines the
  // box grew to 85px, past its 76px rect and onto the hand. Tighter side
  // padding and 16px lines keep three lines inside the rect.
  const tight = !!overlayRect && !layout?.portrait;
  // B3c r2: phones get a 44px Discard button. The portrait box trims its
  // padding to 10px so a one-line label stays 68px (DISCARD_PORTRAIT_H);
  // a longer label grows the box, and its measured height moves it up.
  const phone = !!(layout && (layout.portrait || layout.compact || layout.isMobile));
  // B3c r2: a narrow overlay (460x320 to 480x320) keeps the label to
  // three lines, inside its 76px rect, by leaving out the "Click
  // Discard to confirm." the button beside it already says.
  const shortLabel = tight && overlayRect.w < DISCARD_PROMPT_SHORT_LABEL_W;

  return (
    <div
      ref={boxRef}
      data-testid="discard-prompt"
      role="region"
      aria-label="Dealer discard prompt"
      style={{
        ...(overlayRect ? {
          position: 'absolute',
          left: overlayRect.x,
          top: overlayRect.y,
          width: overlayRect.w,
          zIndex: 20,
          boxSizing: 'border-box',
        } : { marginTop: 12 }),
        padding: tight ? '12px 12px' : layout?.portrait ? '10px 18px' : '12px 18px',
        background: '#1A1422',
        border: '2px solid #F0C040',
        borderRadius: 6,
        color: '#F5F5F0',
        fontFamily: 'inherit',
        display: 'flex',
        alignItems: 'center',
        gap: tight ? 10 : 14,
      }}
    >
      <div
        aria-live="polite"
        aria-atomic="true"
        id="discard-instruction"
        style={tight ? { flex: 1, fontSize: 13, lineHeight: '16px' } : { flex: 1, fontSize: 14 }}
      >
        {disabledReason
          ? <span data-testid="discard-disabled-reason">{disabledReason}</span>
          : enabled
          ? <>You picked <strong data-testid="discard-selected">{cardDesc}</strong>.{shortLabel ? '' : ' Click Discard to confirm.'}</>
          : <>Choose a card to discard.</>}
      </div>
      <button
        ref={buttonRef}
        type="button"
        data-action="discard-confirm"
        disabled={!enabled}
        aria-disabled={!enabled}
        aria-describedby="discard-instruction"
        onClick={onConfirm}
        style={{
          background: enabled ? '#3DBA52' : 'transparent',
          color: enabled ? '#1A1422' : '#5A5A5A',
          border: enabled ? 'none' : '1px solid #5A5A5A',
          borderRadius: 4,
          padding: tight ? '8px 14px' : '8px 18px',
          // Z8: a full 40px touch target, like the bid buttons; 44px on
          // phones (B3c r2).
          minHeight: phone ? 44 : 40,
          fontFamily: 'inherit',
          fontSize: 13,
          fontWeight: 600,
          cursor: enabled ? 'pointer' : 'not-allowed',
        }}
      >
        Discard
      </button>
    </div>
  );
}
